import { useState, useRef } from 'react';
import type { UploadChangeParam, RcFile, UploadFile } from 'antd/es/upload/interface';
import { message } from 'antd';
import { uploadFile } from 'api/common';
import { providerGetKlarityResourcesUrl } from 'api/provider';

export interface FileUploadResult {
    id?: number;
    url?: string;
    name?: string;
    type?: string;
    size?: number;
    previewUrl?: string;
}

export interface UseFileUploadOptions {
    maxSize?: number; // MB
    accept?: string;
    multiple?: boolean;
    onSuccess?: (result: FileUploadResult) => void;
    onError?: (error: Error) => void;
    uploadFn?: (data: FormData) => Promise<FileUploadResult>;
    formData?: Record<string, string>;
    formatFileData?: (result: any) => Promise<FileUploadResult>;
}

export interface UseFileUploadReturn {
    loading: boolean;
    uploadFile: (file: File) => Promise<FileUploadResult | null>;
    validateFile: (file: File) => boolean;
}

export const useFileUpload = (options: UseFileUploadOptions = {}): UseFileUploadReturn => {
    const {
        maxSize = 10,
        accept = '*',
        multiple = false,
        onSuccess,
        onError,
        uploadFn,
        formData,
        formatFileData,
    } = options;

    const [loading, setLoading] = useState(false);
    const fileInfoRef = useRef<UploadChangeParam<UploadFile>>();

    const validateFile = (file: File): boolean => {
        // 检查文件大小
        const isLtSize = file.size / 1024 / 1024 <= maxSize;
        if (!isLtSize) {
            message.error(`File size cannot exceed ${maxSize}MB. Please choose a smaller file.`);
            return false;
        }

        // 检查文件类型
        if (accept !== '*') {
            const acceptedTypes = accept.split(',').map((type) => type.trim());
            const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
            const mimeType = file.type;

            const isAccepted = acceptedTypes.some((type) => {
                if (type.startsWith('.')) {
                    return fileExtension === type.toLowerCase();
                }
                return mimeType.includes(type.replace('*', ''));
            });

            if (!isAccepted) {
                message.error(`不支持的文件类型，请选择: ${accept}`);
                return false;
            }
        }

        return true;
    };

    const uploadFileToServer = async (file: File): Promise<FileUploadResult | null> => {
        if (!validateFile(file)) {
            return null;
        }

        setLoading(true);

        try {
            const formDataValue = new FormData();
            formDataValue.append('file', file);
            if (formData) {
                Object.entries(formData).forEach(([key, value]) => {
                    formDataValue.append(key, value);
                });
            }

            const res = uploadFn ? await uploadFn(formDataValue) : await uploadFile(formDataValue);

            if (!res.error && res.data?.data) {
                if (formatFileData) {
                    const fileData = await formatFileData(res.data.data);
                    onSuccess?.(fileData);
                    return fileData;
                }
                const result: FileUploadResult = {
                    url: res.data.data,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                };

                if (typeof res.data.data === 'string' && !res.data.data.startsWith('https://')) {
                    const resUrl = await providerGetKlarityResourcesUrl(res.data.data);
                    if (!resUrl.error) {
                        result.previewUrl = resUrl.data?.data?.singedUrl;
                    }
                }

                onSuccess?.(result);
                return result;
            } else {
                throw new Error(res.error || '上传失败');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '上传失败';
            message.error(errorMessage);
            onError?.(error instanceof Error ? error : new Error(errorMessage));
            return null;
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        uploadFile: uploadFileToServer,
        validateFile,
    };
};
